import { useQueryClient } from "@tanstack/react-query";
import { set } from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";

import { toast } from "@performyard/py-components";

import type { ChecklistItemSaveData, ItemStatus, ItemType } from "../types";

import { meetingDetailsQueryKeys } from "../query-keys";

type ChecklistItemMeeting = {
	meetingId: string;
	status: "complete" | "incomplete" | "deleted";
};

/**
 * In-memory shape of an agenda or action item as stored in the React Query cache.
 * Server payloads use ISO-string dates; optimistic writes follow the same shape.
 */
export type ChecklistItemRecord = {
	_id: string;
	name: string;
	meetings: ChecklistItemMeeting[];
	created?: { userId?: string };
	participants?: Array<{ userId: string; role: string }>;
	dueDate?: Date | string | null;
	goalId?: string | null;
};

type MutationCallbacks = {
	onSuccess?: () => void;
	onError?: () => void;
	onSettled?: () => void;
};

type AgendaAddParams = {
	data: { name: string; meetings: ChecklistItemMeeting[] };
};
type ActionAddParams = {
	data: {
		name: string;
		meetings: ChecklistItemMeeting[];
		participants: Array<{ userId: string; role: "assignee" }>;
		dueDate: string | null;
	};
};
type AgendaEditParams = { agendaItemId: string; data: Record<string, unknown> };
type ActionEditParams = { actionItemId: string; data: Record<string, unknown> };
type AgendaReorderParams = { meetingId: string; agendaItemIds: string[] };
type ActionReorderParams = { meetingId: string; actionItemIds: string[] };

type AgendaAddMutationFn = (params: AgendaAddParams, options?: MutationCallbacks) => void;
type ActionAddMutationFn = (params: ActionAddParams, options?: MutationCallbacks) => void;
type AgendaEditMutationFn = (params: AgendaEditParams, options?: MutationCallbacks) => void;
type ActionEditMutationFn = (params: ActionEditParams, options?: MutationCallbacks) => void;
type AgendaReorderMutationFn = (params: AgendaReorderParams, options?: MutationCallbacks) => void;
type ActionReorderMutationFn = (params: ActionReorderParams, options?: MutationCallbacks) => void;

type CommonParams = {
	meetingId: string;
	currentUserId: string;
	items: ChecklistItemRecord[] | undefined;
	isLoading: boolean;
	isInteractionDisabled?: boolean;
};

type AgendaParams = CommonParams & {
	itemType: "agenda";
	addItemMutation: AgendaAddMutationFn;
	editItemMutation: AgendaEditMutationFn;
	reorderItemsMutation: AgendaReorderMutationFn;
};

type ActionParams = CommonParams & {
	itemType: "action";
	addItemMutation: ActionAddMutationFn;
	editItemMutation: ActionEditMutationFn;
	reorderItemsMutation: ActionReorderMutationFn;
};

type UseChecklistItemsParams = AgendaParams | ActionParams;

type UseChecklistItemsReturn = {
	activeItems: ChecklistItemRecord[];
	isLoading: boolean;
	isAddingItem: boolean;
	editingItemId: string | null;
	/** True when the section's "Add" button should be disabled (interaction off, or already editing/adding). */
	isAddButtonDisabled: boolean;
	handleAddButtonClick: () => void;
	handleEditStart: (itemId: string) => void;
	handleEditSave: (save: boolean, data?: ChecklistItemSaveData) => void;
	handleNewItemSave: (save: boolean, data?: ChecklistItemSaveData) => void;
	handleStatusChange: (itemId: string, status: ItemStatus) => void;
	handleReorder: (reorderedActiveItems: ChecklistItemRecord[]) => void;
};

const ITEM_TYPE_LABEL = {
	agenda: "Agenda item",
	action: "Action item",
} as const satisfies Record<ItemType, string>;

const isAgendaParams = (params: UseChecklistItemsParams): params is AgendaParams => params.itemType === "agenda";

/** Build the action-item-specific bits of an `add`/`edit` payload. */
const toActionItemPayloadFields = (data: ChecklistItemSaveData) => ({
	participants: data.assigneeId ? [{ userId: data.assigneeId, role: "assignee" as const }] : [],
	dueDate: data.dueDate
		? set(data.dueDate, { hours: 17, minutes: 0, seconds: 0, milliseconds: 0 }).toISOString()
		: null,
});

/**
 * Shared hook for managing checklist items (agenda items and action items).
 *
 * Owns:
 *   - which item (if any) is being edited or newly added,
 *   - the add/edit/status mutation flow with optimistic cache updates so the
 *     UI reflects changes immediately and reverts on error.
 *
 * The inline form (`<ChecklistItemForm />`) owns its own input state, so this
 * hook never mirrors per-keystroke input — it just receives the final payload
 * via `handleEditSave` / `handleNewItemSave`.
 */
export const useChecklistItems = (params: UseChecklistItemsParams): UseChecklistItemsReturn => {
	const { meetingId, currentUserId, itemType, items, isLoading, isInteractionDisabled = false } = params;

	const queryClient = useQueryClient();

	const [isAddingItem, setIsAddingItem] = useState(false);
	const [editingItemId, setEditingItemId] = useState<string | null>(null);
	const [optimisticOrderIds, setOptimisticOrderIds] = useState<string[] | null>(null);
	const reorderSeqRef = useRef(0);

	const itemTypeLabel = ITEM_TYPE_LABEL[itemType];
	const queryKey =
		itemType === "agenda"
			? meetingDetailsQueryKeys.meetingAgendaItems(meetingId)
			: meetingDetailsQueryKeys.meetingActionItems(meetingId);

	/**
	 * Set the cached item list. The hooks return `data?.[0]` so the cache shape
	 * is `[Item[]]` — keep that wrapping consistent on every write.
	 */
	const writeItemsToCache = useCallback(
		(nextItems: ChecklistItemRecord[]): void => {
			queryClient.setQueryData(queryKey, () => [nextItems]);
		},
		[queryClient, queryKey]
	);

	const buildMutationCallbacks = useCallback(
		({
			previousItems,
			successMessage,
			errorMessage,
		}: {
			previousItems: ChecklistItemRecord[];
			successMessage?: string;
			errorMessage: string;
		}): MutationCallbacks => ({
			onSuccess: () => {
				if (successMessage) toast.success(successMessage);
			},
			onError: () => {
				writeItemsToCache(previousItems);
				toast.error(errorMessage);
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey });
			},
		}),
		[queryClient, queryKey, writeItemsToCache]
	);

	const handleSaveEdit = useCallback(
		(itemId: string, data: ChecklistItemSaveData): void => {
			const trimmedName = data.name.trim();
			const currentItem = items?.find((item) => item._id === itemId);
			if (!currentItem) return;

			const previousItems = items ?? [];
			const callbacks = buildMutationCallbacks({
				previousItems,
				errorMessage: `Failed to update ${itemTypeLabel.toLowerCase()}`,
			});

			if (isAgendaParams(params)) {
				if (currentItem.name === trimmedName) return;

				writeItemsToCache(
					previousItems.map((item) => (item._id === itemId ? { ...item, name: trimmedName } : item))
				);

				params.editItemMutation({ agendaItemId: itemId, data: { name: trimmedName } }, callbacks);
				return;
			}

			const { participants: nextParticipants, dueDate: dueDateIso } = toActionItemPayloadFields(data);
			writeItemsToCache(
				previousItems.map((item) =>
					item._id === itemId
						? { ...item, name: trimmedName, participants: nextParticipants, dueDate: dueDateIso }
						: item
				)
			);

			params.editItemMutation(
				{
					actionItemId: itemId,
					data: { name: trimmedName, participants: nextParticipants, dueDate: dueDateIso },
				},
				callbacks
			);
		},
		[buildMutationCallbacks, itemTypeLabel, items, params, writeItemsToCache]
	);

	const handleAddItem = useCallback(
		(data: ChecklistItemSaveData): void => {
			if (isInteractionDisabled) return;

			const trimmedName = data.name.trim();
			if (!trimmedName) return;

			const tempId = `temp-${Date.now()}`;
			const previousItems = items ?? [];
			const baseMeetings: ChecklistItemMeeting[] = [{ meetingId, status: "incomplete" }];

			const callbacks = buildMutationCallbacks({
				previousItems,
				errorMessage: `Failed to create ${itemTypeLabel.toLowerCase()}`,
			});

			if (isAgendaParams(params)) {
				const optimisticItem: ChecklistItemRecord = {
					_id: tempId,
					name: trimmedName,
					meetings: baseMeetings,
					created: { userId: currentUserId },
				};
				writeItemsToCache([...previousItems, optimisticItem]);

				params.addItemMutation({ data: { name: trimmedName, meetings: baseMeetings } }, callbacks);
				return;
			}

			const { participants: nextParticipants, dueDate: dueDateIso } = toActionItemPayloadFields(data);
			const optimisticItem: ChecklistItemRecord = {
				_id: tempId,
				name: trimmedName,
				meetings: baseMeetings,
				participants: nextParticipants,
				dueDate: dueDateIso,
				created: { userId: currentUserId },
			};
			writeItemsToCache([...previousItems, optimisticItem]);

			params.addItemMutation(
				{
					data: {
						name: trimmedName,
						meetings: baseMeetings,
						participants: nextParticipants,
						dueDate: dueDateIso,
					},
				},
				callbacks
			);
		},
		[
			buildMutationCallbacks,
			currentUserId,
			isInteractionDisabled,
			itemTypeLabel,
			items,
			meetingId,
			params,
			writeItemsToCache,
		]
	);

	const handleStatusChange = useCallback(
		(itemId: string, status: ItemStatus): void => {
			if (isInteractionDisabled) return;

			const currentItem = items?.find((item) => item._id === itemId);
			if (!currentItem) return;

			const updatedMeetings = currentItem.meetings.map((m) =>
				m.meetingId === meetingId ? { ...m, status: status as ChecklistItemMeeting["status"] } : m
			);

			const previousItems = items ?? [];
			writeItemsToCache(
				previousItems.map((item) => (item._id === itemId ? { ...item, meetings: updatedMeetings } : item))
			);

			// Toast for delete only — check/uncheck stays quiet.
			const callbacks = buildMutationCallbacks({
				previousItems,
				successMessage: status === "deleted" ? `${itemTypeLabel} deleted` : undefined,
				errorMessage: `Failed to update ${itemTypeLabel.toLowerCase()}`,
			});

			if (isAgendaParams(params)) {
				params.editItemMutation({ agendaItemId: itemId, data: { meetings: updatedMeetings } }, callbacks);
			} else {
				params.editItemMutation({ actionItemId: itemId, data: { meetings: updatedMeetings } }, callbacks);
			}
		},
		[buildMutationCallbacks, isInteractionDisabled, itemTypeLabel, items, meetingId, params, writeItemsToCache]
	);

	const handleReorder = useCallback(
		(reorderedActiveItems: ChecklistItemRecord[]): void => {
			if (isInteractionDisabled) return;

			// Skip optimistic temp ids; the settled refetch reconciles them. Checked
			// before any optimistic write so an all-temp drag is a clean no-op.
			const itemIds = reorderedActiveItems.map((item) => item._id).filter((id) => !id.startsWith("temp-"));
			if (!itemIds.length) return;

			const previousItems = items ?? [];

			// Cache notifications are async — local state reorders synchronously so
			// the drop animation targets the new slot.
			setOptimisticOrderIds(reorderedActiveItems.map((item) => item._id));

			// Keep deleted items (filtered from display) at the end of the cache write.
			const reorderedIds = new Set(reorderedActiveItems.map((item) => item._id));
			const deletedItems = previousItems.filter((item) => !reorderedIds.has(item._id));
			writeItemsToCache([...reorderedActiveItems, ...deletedItems]);

			const callbacks = buildMutationCallbacks({
				previousItems,
				errorMessage: `Failed to reorder ${itemTypeLabel.toLowerCase()}s`,
			});
			// Only the latest drag's callbacks may clear local order or roll back —
			// a superseded reorder settling must not flash an intermediate order.
			const seq = ++reorderSeqRef.current;
			const reorderCallbacks: MutationCallbacks = {
				onSuccess: callbacks.onSuccess,
				onError: () => {
					if (seq !== reorderSeqRef.current) return;
					setOptimisticOrderIds(null);
					callbacks.onError?.();
				},
				onSettled: () => {
					if (seq !== reorderSeqRef.current) return;
					setOptimisticOrderIds(null);
					callbacks.onSettled?.();
				},
			};

			if (isAgendaParams(params)) {
				params.reorderItemsMutation({ meetingId, agendaItemIds: itemIds }, reorderCallbacks);
			} else {
				params.reorderItemsMutation({ meetingId, actionItemIds: itemIds }, reorderCallbacks);
			}
		},
		[buildMutationCallbacks, isInteractionDisabled, itemTypeLabel, items, meetingId, params, writeItemsToCache]
	);

	const handleEditStart = useCallback(
		(itemId: string): void => {
			if (isInteractionDisabled) return;
			setIsAddingItem(false);
			setEditingItemId(itemId);
		},
		[isInteractionDisabled]
	);

	const handleEditSave = useCallback(
		(save: boolean, data?: ChecklistItemSaveData): void => {
			if (save && data && editingItemId && data.name.trim()) {
				handleSaveEdit(editingItemId, data);
			}
			setEditingItemId(null);
		},
		[editingItemId, handleSaveEdit]
	);

	const handleAddButtonClick = useCallback((): void => {
		if (isInteractionDisabled) return;
		setEditingItemId(null);
		setIsAddingItem(true);
	}, [isInteractionDisabled]);

	const handleNewItemSave = useCallback(
		(save: boolean, data?: ChecklistItemSaveData): void => {
			if (save && data && data.name.trim()) {
				handleAddItem(data);
			}
			setIsAddingItem(false);
		},
		[handleAddItem]
	);

	const activeItems = useMemo(() => {
		if (!items) return [];
		const active = items.filter((item) => {
			const meetingItem = item.meetings.find((m) => m.meetingId === meetingId);
			return meetingItem?.status !== "deleted";
		});
		if (!optimisticOrderIds) return active;

		// Unlisted items (created mid-drag) keep their relative order at the end.
		const rank = new Map(optimisticOrderIds.map((id, index) => [id, index]));
		return [...active].sort(
			(a, b) => (rank.get(a._id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b._id) ?? Number.MAX_SAFE_INTEGER)
		);
	}, [items, meetingId, optimisticOrderIds]);

	const isAddButtonDisabled = isInteractionDisabled || editingItemId !== null || isAddingItem;

	return {
		activeItems,
		isLoading,
		isAddingItem,
		editingItemId,
		isAddButtonDisabled,
		handleAddButtonClick,
		handleEditStart,
		handleEditSave,
		handleNewItemSave,
		handleStatusChange,
		handleReorder,
	};
};
